<template>
  <div class="app-modal" :id="props.modalId">
    <transition
      mode="out-in"
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div
        class="app-modal-backdrop"
        ref="alertModal"
        v-if="props.showModal && !isEsc"
      >
        <dialog
          class="app-modal-content"
          :class="[{ '__large': props.isSizedUp }]"
          ref="modalContentRef"
          aria-labelledby="modalHeader"
          open
        >
          <header>
            <h1 id="modalHeader">
              <!-- title: should describe modal -->
              <slot name="heading"></slot>
            </h1>
          </header>
          <main>
            <slot name="content"></slot>
            <!-- main content: any visible HTML elements -->
          </main>
          <footer>
            <!-- buttons, links, etc. -->
            <slot name="footing"></slot>
          </footer>
        </dialog>
      </div>
    </transition>
  </div>
</template>


<script setup>
import { ref, watch, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  modalId: {
    type: String,
    required: true,
  },
  isSizedUp: {
    type: Boolean,
    required: false,
    default: false,
  },
  showModal: {
    type: Boolean,
    required: true,
  },
  allowEsc: {
    type: Boolean,
    required: false,
    default: true,
  },
});

//Emits
const emit = defineEmits(['update:showModal']);

const firstFocusableElement = ref(undefined);
const focusableContent = ref([]);
const lastFocusableElement = ref(undefined);

const isEsc = ref(false);

const bindKeys = (e) => {
  if (props.allowEsc) {
    // check for Esc (close modal) and Tab/Shift-Tab (trap focus) key presses
    const isEscPressed = e.key === 'Escape' || e.keyCode === 27;
    if (isEscPressed) {
      isEsc.value = true;
      emit('update:showModal', false);
    }
  }

  const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
  if (!isTabPressed) {
    return;
  }

  // shift (for Shift + Tab)
  if (e.shiftKey) {
    if (document.activeElement === firstFocusableElement.value) {
      // cycle backward to last focusable
      lastFocusableElement.value.focus();
      e.preventDefault();
    }
  } 
  else if (document.activeElement === lastFocusableElement.value) {    //tab
    // cycle forward to first focusable
    firstFocusableElement.value.focus();  // add focus for the first focusable element
    e.preventDefault();
  }
};

const init = () => {
  // add all the elements inside modal which you want to make focusable
  // e.g. 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const modalElement = document.querySelector(`#${props.modalId}`);
  if (!modalElement) return;

  const focusableElementsSelector = 'button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])';
  const allFocusable = modalElement.querySelectorAll(focusableElementsSelector);

  focusableContent.value = Array.from(allFocusable);
  firstFocusableElement.value = focusableContent.value[0];
  lastFocusableElement.value = focusableContent.value[focusableContent.value.length - 1];

  if (firstFocusableElement.value) {
    firstFocusableElement.value.focus();
  }
};

watch(() => props.showModal, (newVal) => {
  if (newVal) {
    nextTick(() => {
      init();
      window.addEventListener('keydown', bindKeys);
    });
  } else {
    // Clean up when the modal is hidden
    window.removeEventListener('keydown', bindKeys);
  }
});

// A component using this would need to update `isEsc` based on the emit,
// or handle the key event directly. This approach is more robust.
watch(isEsc, (newVal) => {
  if (newVal) {
    // Reset `isEsc` after the modal closes
    // to allow it to be re-opened later.
    isEsc.value = false;
  }
});

// onUnmounted for cleanup
onUnmounted(() => {
  window.removeEventListener('keydown', bindKeys);
});
</script>


<style lang="scss" scoped>
@import "~@ridmv/web-themes/styles/modal";

.app-modal {
  .app-modal-backdrop {
    display: flex;
    justify-content: center;
    align-items: center;
    dialog {
      position: relative;   
      margin: auto;         
      inset: unset;         
      border: none;
      padding: 0;
      max-width: none;
      .app-modal-content {
        &.__overflow {      // add vertical scroll within modal if content gets too long
          height: 100%;
          overflow-y: auto;
          width: 100%;

          @media print {
            width: 220% !important;
            overflow-y: hidden;
            height: 115% !important;
          }
        }
      }
    }
  }

  dialog::backdrop {
    background: transparent;
  }

}

@media only screen and (max-width: 599px) {
  .app-modal {
    .app-modal-backdrop {
      display: flex;
      justify-content: center;
      align-items: center;
      dialog {
        position: relative;   
        margin: auto;         
        inset: unset;         
        border: none;
        padding: 0;
        max-width: none;
        .app-modal-content {
          &.__overflow {      // add vertical scroll within modal if content gets too long
            height: 100vh;
            overflow-y: auto;
            width: 100%;

            @media print {
              width: 220% !important;
              overflow-y: hidden;
              height: 115% !important;
            }
          }
        }
      }
    }
  }
}
</style>
